import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, UserCircle2 } from 'lucide-react';
import { ADMIN_PALETTE } from '../adminDesignSystem';
import { API, StatCard } from './complianceShared';

export function VatGapsTab({ headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/compliance/vat-gaps`, { headers });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load VAT gaps');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    load();
  }, [load]);

  const openEditor = (kind, row) => {
    setEditing({ kind, id: row.id, name: row.name });
    setForm({
      billing_legal_name: row.name || '',
      billing_vat_number: '',
      billing_country_code: row.country_code || 'GR',
      billing_tax_office: '',
      billing_address: '',
    });
  };

  const save = async () => {
    try {
      const url =
        editing.kind === 'venue'
          ? `${API}/admin/compliance/billing/venue/${editing.id}`
          : `${API}/admin/compliance/billing/host/${editing.id}`;
      await axios.patch(url, form, { headers });
      toast.success('Billing details saved');
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  if (loading) return <p style={{ color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>Loading…</p>;
  if (!data) return null;

  const renderMissing = (row, kind) => (
    <li
      key={`${kind}-${row.id}`}
      data-testid={`vatgap-row-${row.id}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 10,
        padding: 12,
        borderRadius: 10,
        background: ADMIN_PALETTE.surface,
        border: `1px solid ${ADMIN_PALETTE.border}`,
        alignItems: 'center',
      }}
    >
      <div>
        <strong style={{ fontSize: 13, color: ADMIN_PALETTE.ink }}>{row.name || row.email}</strong>
        <p style={{ margin: '2px 0 0', fontSize: 12, color: ADMIN_PALETTE.inkMuted }}>
          {kind === 'venue' ? 'Venue' : 'Host'} · Country {row.country_code || 'GR'}
        </p>
      </div>
      <button
        type="button"
        onClick={() => openEditor(kind, row)}
        data-testid={`vatgap-set-${row.id}`}
        style={{
          border: `1px solid ${ADMIN_PALETTE.brandRed}`,
          background: ADMIN_PALETTE.brandRed,
          color: '#FFFFFF',
          padding: '6px 12px',
          borderRadius: 8,
          cursor: 'pointer',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        Set VAT
      </button>
    </li>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ margin: 0, fontSize: 13, color: ADMIN_PALETTE.inkMuted }}>
        Sellers missing a VAT identifier. Their monthly invoices will be issued with the buyer VAT
        field blank until this is filled — which risks being non-compliant for Greek B2B.
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <StatCard icon={Building2} label="Venues missing VAT" value={data.counts.venues} tone={data.counts.venues ? 'warn' : 'ok'} testid="vatgap-stat-venues" />
        <StatCard icon={UserCircle2} label="Hosts missing VAT" value={data.counts.hosts} tone={data.counts.hosts ? 'warn' : 'ok'} testid="vatgap-stat-hosts" />
      </div>

      <h3 style={{ margin: '10px 0 6px', fontFamily: "'Inria Serif', serif", fontSize: 17 }}>Venues</h3>
      {data.venues_missing_vat.length === 0 ? (
        <p style={{ color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>All venues have a VAT number on file 🎉</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {data.venues_missing_vat.map((v) => renderMissing(v, 'venue'))}
        </ul>
      )}

      <h3 style={{ margin: '14px 0 6px', fontFamily: "'Inria Serif', serif", fontSize: 17 }}>Hosts</h3>
      {data.hosts_missing_vat.length === 0 ? (
        <p style={{ color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>All hosts have a VAT number on file 🎉</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {data.hosts_missing_vat.map((h) => renderMissing(h, 'host'))}
        </ul>
      )}

      {editing && (
        <div
          data-testid="vatgap-modal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,17,21,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: 16,
              padding: 24,
              maxWidth: 520,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            <h3 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 20 }}>
              Set billing details — {editing.name}
            </h3>
            {['billing_legal_name', 'billing_vat_number', 'billing_country_code', 'billing_tax_office', 'billing_address'].map(
              (k) => (
                <div key={k}>
                  <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: ADMIN_PALETTE.inkMuted, letterSpacing: 0.4 }}>
                    {k.replace('billing_', '').replace('_', ' ')}
                  </label>
                  <input
                    value={form[k] || ''}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    data-testid={`vatgap-modal-input-${k}`}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: `1px solid ${ADMIN_PALETTE.border}`,
                      fontSize: 14,
                      marginTop: 4,
                      background: ADMIN_PALETTE.surfaceMuted,
                    }}
                  />
                </div>
              ),
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                type="button"
                onClick={() => setEditing(null)}
                data-testid="vatgap-modal-cancel"
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: `1px solid ${ADMIN_PALETTE.border}`,
                  background: ADMIN_PALETTE.surface,
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                data-testid="vatgap-modal-save"
                style={{
                  padding: '10px 16px',
                  borderRadius: 10,
                  border: 'none',
                  background: ADMIN_PALETTE.ink,
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
